'use client';

import React, {useState} from 'react';
import Image from 'next/image';
import {CourseRibbonWrapper} from '@udemy-v2/react-browse-course';
import styles from './course-lede.module.scss';
import classNames from 'classnames';
import {CourseLanguage} from '../course-languages/course-language.react-component';
import {BreadcrumbNavigation} from '../breadcrumb-navigation/breadcrumb-navigation.react-component';
import {InstructorDisplay} from '../instructor-display/instructor-display.react-component';
import {RatingDisplay} from '@/features/course-landing-page/components/rating-display/rating-display.react-component';
import {EnrollmentCount} from '@/features/course-landing-page/components/enrollment-count/enrollment-count.react-component';
import {LastUpdated} from '../last-updated/last-updated.react-component';
import {CourseCaptionDetails} from '../course-caption-details/course-caption-details.react-component';
import {useClpCourse} from '../../context/clp-course-provider.react-component';
import {CoursePreviewEntryPointWrapper} from '@/features/course-landing-page/components/course-preview-entry-point/course-preview-entry-point.react-component';
import {AlternateRedirect} from '../alternate-redirect/alternate-redirect.react-component';

import {useCommonData} from '@udemy-v2/common-data';
import {PositionMarkerContainer} from '@/components/position-marker';
import {GoogleCertificateRibbon} from '../google-certificate-ribbon/google-certificate-ribbon.react-component';
import {useCourseCareerAccelerator} from '../../context/career-accelerator-provider.react-component';
import {useI18n} from '@udemy-v2/i18n';
import {useFeatures} from '@udemy-v2/react-experimentation';
import {useSubsDiff} from '../subs-diff/use-subs-diff.react-component';

const PREVIEW_UI_REGION = 'introductionAsset';

export interface CourseLedeProps {
    className?: string;
    hideRating?: boolean;
    hideEnrollments?: boolean;
}

/**
 * Displays the main course header information on CLP
 */
export const CourseLede: React.FC<CourseLedeProps> = ({
    className,
    hideRating = false,
    hideEnrollments = false,
}) => {
    const {gettext} = useI18n();
    const [isCaptionsExpanded, setIsCaptionsExpanded] = useState(false);
    const course = useClpCourse();
    const commonData = useCommonData();
    const hasOrganization = !!commonData.organization;
    const isGrowWithGoogleCourse = !!course.contentCollectionInfo?.isInGooglePartnerCollection;
    const courseCareerAccelerator = useCourseCareerAccelerator();
    const {isLoading, featureVariantAssignments} = useFeatures(['clp_outcome_above_video']);
    const isClpOutcomeAboveVideoEnabled =
        featureVariantAssignments.get('clp_outcome_above_video')?.configuration
            ?.showClpOutcomeAboveVideo ?? false;
    const {SubsDiffComponent, shouldShowSubsDiff} = useSubsDiff();
    const handleCaptionsExpandChange = (expanded: boolean) => {
        setIsCaptionsExpanded(expanded);
    };
    const primaryCategory =
        course.categories && course.categories.length > 0 ? course.categories[0] : undefined;

    // AlternateRedirect: wire source_course_id / ?altsc= and pass details when backend + URL params are ready.
    return (
        <PositionMarkerContainer threshold={0.1}>
            <section className={classNames(styles['course-lede'], className)}>
                <div className={styles.container}>
                    {/* Breadcrumb Navigation */}
                    {!hasOrganization && (
                        <BreadcrumbNavigation
                            ariaLabel={gettext('Course topics')}
                            primaryCategory={primaryCategory}
                            primaryTopic={course.primaryTopic}
                        />
                    )}

                    <AlternateRedirect
                    /*
                     * TODO: Add source_course_id.
                     *
                     * Renders "upgraded" message when:
                     * URL: /course/new-course/?altsc=source_course_id
                     * Database has: CourseAlternateRedirect(source_id=source_course_id, destination=new-course, reason="upgraded")
                     *
                     * Renders "unsupported" message when:
                     * URL: /course/alternative-course/?altsc=source_course_id
                     * Database has: CourseAlternateRedirect(source_id=source_course_id, destination=alternative-course, reason="unsupported")
                     */
                    // details={{
                    //     reason: ALTERNATE_REDIRECT_REASON.UPGRADED,
                    //     sourceCourseTitle: 'Test Course',
                    // }}
                    />

                    {!isLoading && isClpOutcomeAboveVideoEnabled && (
                        <div className={styles['above-video-container']}>
                            <h1 className={classNames(styles.title, 'ud-heading-xxl')}>
                                {course.title}
                            </h1>
                            {isGrowWithGoogleCourse && (
                                <>
                                    <GoogleCertificateRibbon
                                        courseCareerAccelerator={courseCareerAccelerator}
                                        firstCourseId={course.id}
                                    />
                                </>
                            )}
                            {course.headline && (
                                <div
                                    className={classNames(
                                        shouldShowSubsDiff
                                            ? styles.headline
                                            : styles['headline-no-subs'],
                                        'ud-text-lg',
                                    )}
                                >
                                    {course.headline}
                                </div>
                            )}
                            {shouldShowSubsDiff ? (
                                <div className={styles['subs-diff-container']}>
                                    <SubsDiffComponent />
                                </div>
                            ) : (
                                <div className={styles['metadata-container']}>
                                    {!hasOrganization && !isGrowWithGoogleCourse && (
                                        <CourseRibbonWrapper
                                            course={course}
                                            numberOfBadgesToShow={2}
                                        />
                                    )}

                                    <div>
                                        {/* Rating Display */}
                                        {!hideRating && course.rating?.average && (
                                            <RatingDisplay
                                                average={course.rating.average}
                                                count={course.rating.count}
                                            />
                                        )}
                                    </div>

                                    <div>
                                        {/* Student Count */}
                                        {!hideEnrollments && course.enrollments?.roundedCount && (
                                            <EnrollmentCount count={course.enrollments.count} />
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Course Preview Entry Point - Only visible on mobile */}
                    <CoursePreviewEntryPointWrapper
                        uiRegion={PREVIEW_UI_REGION}
                        hideWhenLedeOutOfView={false}
                    />

                    {/* Google Logo for Grow with Google courses */}
                    {isGrowWithGoogleCourse && (
                        <Image
                            src="https://frontends.udemycdn.com/staticx/udemy/images/grow-with-google/google-logo.webp"
                            alt="Google Logo"
                            width={98}
                            height={32}
                            className={styles['google-logo']}
                        />
                    )}

                    {/* Course Title */}
                    {!isLoading && !isClpOutcomeAboveVideoEnabled && (
                        <>
                            <h1 className={classNames(styles.title, 'ud-heading-xxl')}>
                                {course.title}
                            </h1>

                            {isGrowWithGoogleCourse && (
                                <>
                                    <GoogleCertificateRibbon
                                        courseCareerAccelerator={courseCareerAccelerator}
                                        firstCourseId={course.id}
                                    />
                                </>
                            )}
                            {course.headline && (
                                <div className={classNames(styles.headline, 'ud-text-lg')}>
                                    {course.headline}
                                </div>
                            )}
                            <div className={styles['metadata-container']}>
                                {!hasOrganization && !isGrowWithGoogleCourse && (
                                    <CourseRibbonWrapper course={course} numberOfBadgesToShow={2} />
                                )}

                                <div>
                                    {/* Rating Display */}
                                    {!hideRating && course.rating?.average && (
                                        <RatingDisplay
                                            average={course.rating.average}
                                            count={course.rating.count}
                                        />
                                    )}
                                </div>

                                <div>
                                    {/* Student Count */}
                                    {!hideEnrollments && course.enrollments?.roundedCount && (
                                        <EnrollmentCount count={course.enrollments.count} />
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {/* Instructor Information */}
                    <div className={styles['instructor-section']}>
                        <InstructorDisplay
                            instructors={course.instructors}
                            isAGwGCourse={isGrowWithGoogleCourse}
                        />
                    </div>

                    <div
                        className={classNames(styles['last-updated-languages-container'], {
                            [styles['expanded-captions']]: isCaptionsExpanded,
                        })}
                    >
                        {/* Last Updated Text */}
                        <LastUpdated />
                        {/* Course Languages */}
                        <CourseLanguage courseLocale={course.locale} />
                        {/* Course Captions */}
                        <CourseCaptionDetails
                            captionedLanguages={course.captions?.map((caption) => caption) || []}
                            isExpanded={isCaptionsExpanded}
                            onExpandChange={handleCaptionsExpandChange}
                        />
                    </div>
                </div>
            </section>
        </PositionMarkerContainer>
    );
};
